import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  Map,
  Source,
  Layer,
  FullscreenControl,
  NavigationControl,
} from "@vis.gl/react-maplibre";

import { Flex, Button, Modal } from "antd";

import PencilAltIcon from "@/icons/PencilAltIcon";
import TrashIcon from "@/icons/TrashIcon";

import PolygonOnMapModal from "@/components/modals/PolygonOnMapModal";

import {
  MAP_STYLES,
  DEFAULT_VIEW_STATE,
  POLYGON_OPACITY,
  POLYGON_STROKE_WIDTH,
  FIT_BOUNDS_PADDING,
} from "@/constants/map";

import { usePolygonPreview } from "../hooks/usePolygonPreview";

const PolygonField = ({
  field,
  value,
  miniature = false,
  width = null,
  height = null,
  showEmpty = false,
  isEdit = false,
  color = "#e66a25",
  objectUuid,
  onChange,
  onScreenshot = null,
  onComplete,
  onKeyDown,
}) => {
  const mapStyle = {
    width: width ? width : miniature ? "230px" : "450px",
    height: height ? height : miniature ? "160px" : "300px",
  };

  const [viewState, setViewState] = useState(DEFAULT_VIEW_STATE);

  const [showEditModal, setShowEditModal] = useState(false);

  const editButtonRef = useRef(null);

  const mapRef = useRef(null);

  // Преобразуем координаты полигона в GeoJSON
  const polygonGeoJSON = useMemo(() => {
    if (!value) return null;

    if (Array.isArray(value)) {
      if (!value.length) return null;

      const coordinates = value.map(({ lat, lon }) => [lon, lat]);
      // Замыкаем полигон, если первая и последняя точки не совпадают
      if (
        coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
        coordinates[0][1] !== coordinates[coordinates.length - 1][1]
      ) {
        coordinates.push(coordinates[0]);
      }

      return {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [coordinates],
        },
      };
    }

    if (value.polygons?.length) {
      const multiCoordinates = value.polygons.map((polygon) => {
        const coords = polygon.map(({ lat, lon }) => [lon, lat]);

        if (
          coords[0][0] !== coords[coords.length - 1][0] ||
          coords[0][1] !== coords[coords.length - 1][1]
        ) {
          coords.push(coords[0]);
        }

        return [coords];
      });

      return {
        type: "Feature",
        geometry: {
          type: "MultiPolygon",
          coordinates: multiCoordinates,
        },
      };
    }

    return null;
  }, [value]);

  const screenshot = usePolygonPreview(
    polygonGeoJSON,
    {
      enabled: miniature || !!onScreenshot,
    },
    color,
    onScreenshot,
  );

  const bounds = useMemo(() => {
    if (!polygonGeoJSON) return null;

    let allCoords = [];

    const { type, coordinates } = polygonGeoJSON.geometry;

    if (type === "Polygon") {
      allCoords = coordinates[0];
    }

    if (type === "MultiPolygon") {
      coordinates.forEach((polygon) => {
        polygon.forEach((ring) => {
          allCoords.push(...ring);
        });
      });
    }

    if (!allCoords.length) return null;

    let minLng = allCoords[0][0],
      maxLng = allCoords[0][0];
    let minLat = allCoords[0][1],
      maxLat = allCoords[0][1];

    allCoords.forEach(([lng, lat]) => {
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });

    return [
      [minLng, minLat], // southwest
      [maxLng, maxLat], // northeast
    ];
  }, [polygonGeoJSON]);

  const fitMapToPolygon = useCallback(
    (duration = 300) => {
      const map = mapRef.current?.getMap?.();

      if (!map || !bounds) return;

      map.fitBounds(bounds, {
        padding: FIT_BOUNDS_PADDING,
        duration,
      });
    },
    [bounds],
  );

  useEffect(() => {
    fitMapToPolygon();
  }, [fitMapToPolygon]);

  const onMapLoad = useCallback(() => {
    fitMapToPolygon(0);
  }, [fitMapToPolygon]);

  const handleClear = () => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить полигон?`,
      content: "Это действие нельзя будет отменить.",

      onOk() {
        onChange(null);
      },
      okText: "Удалить",
      cancelText: "Отмена",
      zIndex: 99999,
    });
  };

  return (
    <>
      <Flex
        gap={20}
        wrap
        align="start"
        justify="space-between"
        style={miniature ? { pointerEvents: "none" } : {}}
      >
        {(value || showEmpty) &&
          (miniature ? (
            screenshot ? (
              <img
                src={screenshot}
                style={{
                  ...mapStyle,
                  objectFit: "cover",
                }}
                alt="polygon preview"
              />
            ) : (
              <div style={mapStyle}>Загрузка...</div>
            )
          ) : (
            <Map
              ref={mapRef}
              {...viewState}
              style={mapStyle}
              mapStyle={MAP_STYLES.voyager}
              attributionControl={false}
              interactive
              onMove={(evt) => setViewState(evt.viewState)}
              onLoad={onMapLoad}
            >
              <>
                <FullscreenControl />
                <NavigationControl showCompass={false} />
              </>

              {polygonGeoJSON && (
                <Source
                  id="polygon-source"
                  type="geojson"
                  data={polygonGeoJSON}
                >
                  <Layer
                    id="polygon-fill"
                    type="fill"
                    paint={{
                      "fill-color": color,
                      "fill-opacity": POLYGON_OPACITY,
                    }}
                  />
                  <Layer
                    id="polygon-outline"
                    type="line"
                    paint={{
                      "line-color": color,
                      "line-width": POLYGON_STROKE_WIDTH,
                    }}
                  />
                </Source>
              )}
            </Map>
          ))}
        {!miniature && isEdit && (
          <Flex vertical gap={10}>
            <Button
              data-focus-edit-button="true"
              icon={<PencilAltIcon />}
              onClick={() => setShowEditModal(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowEditModal(true);
                } else {
                  onKeyDown?.(e);
                }
              }}
              ref={editButtonRef}
            >
              Редактировать
            </Button>
            {value && (
              <Button icon={<TrashIcon />} onClick={handleClear}>
                Очистить
              </Button>
            )}
          </Flex>
        )}
      </Flex>

      {showEditModal && (
        <PolygonOnMapModal
          field={field}
          data={value}
          visible={showEditModal}
          onSave={(val) => {
            setShowEditModal(false);
            onChange?.(val);
            onComplete?.(val);
          }}
          onCancel={() => {
            setShowEditModal(false);
            requestAnimationFrame(() => {
              editButtonRef.current?.focus();
            });
          }}
          color={color}
          objectUuid={objectUuid}
        />
      )}
    </>
  );
};

export default PolygonField;
